import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, Pager, ArticleCard, SkelGrid, SectionNote,
  useReveal, useScrollProgress, useDebounced,
  excerptOf, truncate, formatDate, langPath, localized, localizedImage,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   TOUTACTICLE — the article archive
   ───────────────────────────────────────────────────────────────────────────
   Same data, same endpoint, same filters. What changed:

   · Built on the shared CaciUI design system, so it matches the new homepage
     and a token change lands on both.
   · Filters live in the URL. /toutacticle?q=export&page=3 is now a real,
     shareable, back-button-able address; previously every search was lost on
     refresh and the browser Back button walked out of the page entirely.
   · The list no longer blanks out while you type. Results dim and a spinner
     appears in the field, but the previous page stays readable — the old
     version replaced everything with a centred spinner on every keystroke.
   · Real pagination: disabled buttons at the ends. The old markup put a
     `disabled` attribute on <a> tags, which does nothing, so "previous" stayed
     clickable on page 1 and fired a request for page 0.
   · Empty and failed are now different states. A search with no matches is a
     normal outcome; a dead endpoint gets a retry button.
   · Filter bar is sticky and thumb-reachable, cards are 100% tappable.
   ═══════════════════════════════════════════════════════════════════════════ */

const PER_PAGE_OPTIONS = [6, 12, 24, 48];

/* The API's `perStatus` is an opaque 1/2/3. Naming it here means the JSX reads
   as intent rather than as magic numbers. */
const STATUS = { ACTIVE: "1", ARCHIVE: "2", ALL: "3" };

function Toutacticle() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  /* ── URL is the source of truth ────────────────────────────────────────
     Keeping filters in the query string costs nothing and buys back the
     browser: refresh, Back, and sharing a filtered view all work. */
  const [params, setParams] = useSearchParams();

  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const perPage = PER_PAGE_OPTIONS.includes(Number(params.get("per")))
    ? Number(params.get("per"))
    : 12;
  const status = [STATUS.ACTIVE, STATUS.ARCHIVE, STATUS.ALL].includes(params.get("status"))
    ? params.get("status")
    : STATUS.ACTIVE;
  const query = params.get("q") || "";

  /* The input is local so typing stays instant; the URL only catches up once
     the debounce settles, which also keeps history from filling with one
     entry per keystroke. */
  const [term, setTerm] = useState(query);
  const debounced = useDebounced(term, 400);

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [refetching, setRefetching] = useState(false);

  const cancelRef = useRef(null);
  const alive = useRef(true);
  const firstRun = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  /* Write the settled search term back into the URL, and reset to page 1 —
     staying on page 7 of a different result set shows an empty screen. */
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (debounced === query) return;
    const next = new URLSearchParams(params);
    if (debounced) next.set("q", debounced); else next.delete("q");
    next.delete("page");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const patch = useCallback((changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    if (resetPage) next.delete("page");
    setParams(next);
  }, [params, setParams]);

  /* ── Fetch ─────────────────────────────────────────────────────────────
     `refetching` (rather than `loading`) on every run after the first, so the
     current results stay on screen and only dim. Replacing a full page of
     articles with a spinner on each keystroke is what made the old version
     feel like it was thrashing. */
  const fetchArticles = useCallback(() => {
    if (cancelRef.current) cancelRef.current.cancel("superseded");
    cancelRef.current = axios.CancelToken.source();

    setState((s) => (s === "ready" ? s : "loading"));
    setRefetching(true);

    return axios
      .get(`${BASE_URL}/api/articles`, {
        params: { page, per_page: perPage, perStatus: status, search: query },
        cancelToken: cancelRef.current.token,
      })
      .then((res) => {
        if (!alive.current) return;
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        const pg = res.data?.pagination || {};
        setArticles(data);
        setTotal(Number(pg.total ?? data.length) || 0);
        setTotalPages(Math.max(1, Number(pg.last_page) || 1));
        setState("ready");
      })
      .catch((err) => {
        if (axios.isCancel(err) || !alive.current) return;
        console.error("Toutacticle: failed to load articles —", err);
        setState("error");
      })
      .finally(() => {
        if (alive.current) setRefetching(false);
      });
  }, [page, perPage, status, query]);

  useEffect(() => {
    fetchArticles();
    return () => cancelRef.current && cancelRef.current.cancel("unmounted");
  }, [fetchArticles]);

  const pageRef = useReveal([articles, state]);
  const progress = useScrollProgress();

  /* Move the viewport to the top of the results, not the top of the document:
     paging should not make the visitor scroll past the header again. */
  const listRef = useRef(null);
  const goToPage = (p) => {
    patch({ page: p === 1 ? null : p }, { resetPage: false });
    const el = listRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  const clearAll = () => {
    setTerm("");
    setParams(new URLSearchParams(), { replace: true });
  };

  const hasFilters = Boolean(query) || status !== STATUS.ACTIVE || perPage !== 12;

  const titleOf = (a) => truncate(localized(a, "name", lang), 78);
  const imageOf = (a) => localizedImage(a, "image", lang, BASE_URL);
  const excerptFor = (a) => excerptOf(localized(a, "description", lang), 20);

  const pageTitle = t("menu.Toutacticle");
  const home = langPath(lang, "/");

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{pageTitle} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.Toutacticle", pageTitle)} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t("menu.Toutacticle")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={pageTitle}
        crumbs={[{ label: t("menu.home"), to: home }, { label: pageTitle }]}
        count={
          state === "ready" && total > 0
            ? { value: total.toLocaleString(lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR"),
                label: t("search.results", "résultats") }
            : null
        }
      />

      {/* ══════════════════ FILTERS ══════════════════ */}
      <div className="bar">
        <div className="caci-shell">
          <div className="bar-inner">
            <div className="field">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t("search.placeholder")}
                aria-label={t("search.placeholder")}
                dir="auto"
              />
              {refetching && <span className="field-busy" aria-hidden="true" />}
              {term && (
                <button
                  type="button"
                  className="field-clear"
                  onClick={() => setTerm("")}
                  aria-label={t("home.Close")}
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>

            <div className="bar-filters">
              <div className="select">
                <select
                  value={status}
                  onChange={(e) => patch({ status: e.target.value })}
                  aria-label={t("search.active")}
                >
                  <option value={STATUS.ACTIVE}>{t("search.active")}</option>
                  <option value={STATUS.ARCHIVE}>{t("search.archive")}</option>
                  <option value={STATUS.ALL}>{t("search.touts_page")}</option>
                </select>
              </div>
              <div className="select">
                <select
                  value={perPage}
                  onChange={(e) => patch({ per: e.target.value })}
                  aria-label={t("search.perPage")}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} {t("search.perPage")}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {hasFilters && (
            <div className="bar-active">
              {query && (
                <span className="chip">
                  <i className="bi bi-search" />
                  <span dir="auto">{truncate(query, 34)}</span>
                </span>
              )}
              {status !== STATUS.ACTIVE && (
                <span className="chip">
                  <i className="bi bi-funnel" />
                  {status === STATUS.ARCHIVE ? t("search.archive") : t("search.touts_page")}
                </span>
              )}
              <button type="button" className="chip-clear" onClick={clearAll}>
                <i className="bi bi-x-circle" />
                {t("search.clear", "Tout effacer")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════ RESULTS ══════════════════ */}
      <section className="caci-section" ref={listRef}>
        <div className="caci-shell">
          {state === "loading" && <SkelGrid count={perPage > 12 ? 12 : perPage} />}

          {state === "error" && (
            <SectionNote
              state="error"
              title={t("search.errorTitle", "Chargement impossible")}
              text={t("home.section_error", "Cette section n'a pas pu être chargée.")}
              action={
                <button type="button" className="btn-quiet" onClick={fetchArticles}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {state === "ready" && articles.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                hasFilters ? (
                  <button type="button" className="btn-quiet" onClick={clearAll}>
                    <i className="bi bi-arrow-counterclockwise" />
                    {t("search.clear", "Tout effacer")}
                  </button>
                ) : null
              }
            />
          )}

          {state === "ready" && articles.length > 0 && (
            <>
              <div className={`caci-grid${refetching ? " is-busy" : ""}`}>
                {articles.map((a, i) => (
                  <ArticleCard
                    key={a.id ?? i}
                    index={i}
                    to={`/article/${a.id}/${a.slug || ""}`}
                    image={imageOf(a)}
                    title={titleOf(a)}
                    excerpt={excerptFor(a)}
                    date={formatDate(a.starts_at, lang, t("home.UnknownDate"))}
                    categories={(a.cat_articles || [])
                      .filter((ca) => ca?.category)
                      .map((ca) => ({
                        id: ca.category.id,
                        to: `/category/${ca.category.id}`,
                        label: truncate(localized(ca.category, "name", lang), 26),
                      }))}
                    readLabel={t("home.Read_More")}
                    isRtl={isRtl}
                  />
                ))}
              </div>

              <div className="pager-wrap">
                <Pager
                  page={page}
                  totalPages={totalPages}
                  onChange={goToPage}
                  labels={{
                    first: t("search.first", "Première page"),
                    prev: t("search.prev", "Page précédente"),
                    next: t("search.next", "Page suivante"),
                    last: t("search.last", "Dernière page"),
                    page: t("search.page", "Page"),
                    of: t("search.of", "sur"),
                  }}
                />
              </div>
            </>
          )}

          {/* Screen readers get told the count changed; sighted users see it
              in the header band. */}
          <p className="caci-sr" aria-live="polite">
            {state === "ready" ? `${total} ${t("search.results", "résultats")}` : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

/* Page-specific styles: everything else comes from CaciUI. */
const PAGE_CSS = `
.caci-page .bar-active {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  margin-block-start: 12px;
}
.caci-page .chip {
  display: inline-flex; align-items: center; gap: 7px;
  min-height: 34px; padding: 0 14px; border-radius: 999px;
  background: var(--mint); color: var(--pine);
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .05em; max-width: 100%;
}
.caci-page .chip > span {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.caci-page .chip i { font-size: 11px; opacity: .7; flex: 0 0 auto; }
.caci-page .chip-clear {
  display: inline-flex; align-items: center; gap: 7px;
  min-height: 34px; padding: 0 14px; border-radius: 999px; cursor: pointer;
  background: transparent; border: 1px solid var(--rule-2); color: var(--muted);
  font-family: var(--mono); font-size: 11px; font-weight: 600; letter-spacing: .05em;
  transition: color var(--t1) var(--e), border-color var(--t1) var(--e);
}
.caci-page .chip-clear:hover { color: var(--danger); border-color: rgba(163,58,43,.4); }

/* Refetching dims the list instead of destroying it, so the page never
   flashes empty between keystrokes. */
.caci-page .caci-grid.is-busy {
  opacity: .45; pointer-events: none;
  transition: opacity .2s ease;
}
.caci-page .pager-wrap { margin-block-start: clamp(36px, 6vw, 64px); }
`;

export default Toutacticle;
